<template>
  <v-container>
    <v-row>
      <v-col cols="12" md="4">
        <h4>Define pollen sum:</h4>
      </v-col>
      <v-col cols="12" md="8">
        <v-row>
          <v-radio-group
            v-model="getDefaultPollenSum"
            @change="(e) => setDefaultPollenSum(e)"
          >
            <v-radio
              color="black"
              :value="true"
              label="Default pollen sum (pollen + spores)"
            ></v-radio>
            <v-radio
              color="black"
              :value="false"
              label="Custom pollen sum"
            ></v-radio>
          </v-radio-group>
        </v-row>
        <v-row v-if="getDefaultPollenSum" align="start" justify="start">
          <v-btn dark> Next step <v-icon right>mdi-skip-next</v-icon> </v-btn>
        </v-row>
      </v-col>
    </v-row>

    <v-divider></v-divider>

    <!-- POLLEN HABITS form -->
    <v-row v-if="!getDefaultPollenSum">
      <v-col cols="12" md="4">
        <h4>Filter Pollen Habit</h4>
      </v-col>
      <v-col cols="12" md="8">
        <v-form v-model="getPollenHabitsForm.validated">
          <v-row>
            <v-radio-group :disabled="getPollenHabitsForm.completed" v-model="getPollenHabitsForm.mode">
              <v-radio
                color="black"
                value="default"
                label="Select all pollen habits"
              ></v-radio>

              <v-radio
                color="black"
                value="add"
                label="Add single pollen habit(s) to selection"
              ></v-radio>

              <v-radio
                color="black"
                value="discard"
                label="Discard single pollen habit(s) from selection"
              ></v-radio>
            </v-radio-group>
          </v-row>
          <!-- Form to add pollen habits to selection -->
          <v-row v-if="getPollenHabitsForm.mode === 'add'">
            <v-combobox
              outlined
              multiple
              :disabled="getPollenHabitsForm.completed"
              :label="
                getPollenHabitsForm.selected.length +
                ' pollen habits added to selection.'
              "
              :items="getPollenHabits"
              v-model="getPollenHabitsForm.selected"
              chips
              item-color="black"
              :rules="[required]"
              deletable-chips
              item-text="pollen_habit_name"
              item-value="id_pollen_habit"
            >
              <template v-slot:selection="{ item }">
                <v-chip
                  close
                  dark
                  color="success"
                  @click:close="
                    getPollenHabitsForm.selected.splice(
                      getPollenHabitsForm.selected.indexOf(item),
                      1
                    )
                  "
                  >{{ item.pollen_habit_name }}</v-chip
                >
              </template></v-combobox
            >
          </v-row>
          <v-row v-if="getPollenHabitsForm.mode === 'discard'">
            <v-autocomplete
              outlined
              multiple
              :disabled="getPollenHabitsForm.completed"
              :label="
                getPollenHabitsForm.discarded.length +
                ' pollen habits to discard from selection'
              "
              :items="getPollenHabits"
              v-model="getPollenHabitsForm.discarded"
              chips
              item-color="black"
              :rules="[required]"
              deletable-chips
              item-text="pollen_habit_name"
              item-value="id_pollen_habit"
            >
              <template v-slot:selection="{ item }">
                <v-chip
                  close
                  dark
                  color="error"
                  @click:close="
                    getPollenHabitsForm.discarded.splice(
                      getPollenHabitsForm.discarded.indexOf(item),
                      1
                    )
                  "
                  >{{ item.pollen_habit_name }}</v-chip
                >
              </template></v-autocomplete
            >
          </v-row>
        </v-form>
        <v-row
          v-if="!getPollenHabitsForm.completed"
          align="start"
          justify="start"
        >
          <v-btn
            :dark="getPollenHabitsForm.validated"
            :disabled="!getPollenHabitsForm.validated"
            @click="getPollenHabitsForm.completed = true"
          >
            Continue
          </v-btn>
        </v-row>
      </v-col>
    </v-row>

    <!-- TAXON FAMILY form -->
    <v-row
      v-if="!getDefaultPollenSum && getPollenHabitsForm.validated && getPollenHabitsForm.completed"
    >
      <v-col cols="12" md="4">
        <h4>Filter by Family</h4>
      </v-col>
      <v-col cols="12" md="8">
        <v-form v-model="isFormValid">
          <v-row>
            <v-radio-group v-model="fam_choice">
              <v-radio
                color="black"
                value="1"
                label="Select all families corresponding to filters"
              ></v-radio>

              <v-radio
                color="black"
                value="2"
                label="Add single family(ies) to selection"
              ></v-radio>

              <v-radio
                color="black"
                value="3"
                label="Discard single family(ies) from selection"
              ></v-radio>
            </v-radio-group>
          </v-row>
          <!-- Form to add pollen habits to selection -->
          <v-row v-if="fam_choice === '2'">
            <v-combobox
              outlined
              multiple
              :label="
                getSelectedPollenHabits.length +
                ' pollen habits added to selection.'
              "
              :items="getTaxonFamilies"
              v-model="getSelectedPollenHabits"
              chips
              item-color="black"
              :rules="[required]"
              deletable-chips
              item-text="family_name"
              item-value="id_family"
              @input="
                (e) => {
                  setSelectedPollenHabits(e);
                }
              "
            >
              <template v-slot:selection="{ item }">
                <v-chip
                  close
                  dark
                  color="success"
                  @click:close="
                    getSelectedPollenHabits.splice(
                      getSelectedPollenHabits.indexOf(item),
                      1
                    )
                  "
                  >{{ item.family_name }}</v-chip
                >
              </template></v-combobox
            >
          </v-row>
          <v-row v-if="fam_choice === '3'">
            <v-autocomplete
              outlined
              multiple
              :label="
                getDiscardedPollenHabits.length +
                ' pollen habits to discard from selection'
              "
              :items="getTaxonFamilies"
              v-model="getDiscardedPollenHabits"
              chips
              item-color="black"
              :rules="[required]"
              deletable-chips
              item-text="family_name"
              item-value="id_family"
              @input="
                (e) => {
                  setDiscardedPollenHabits(e);
                }
              "
            >
              <template v-slot:selection="{ item }">
                <v-chip
                  close
                  dark
                  color="error"
                  @click:close="
                    getDiscardedPollenHabits.splice(
                      getDiscardedPollenHabits.indexOf(item),
                      1
                    )
                  "
                  >{{ item.family_name }}</v-chip
                >
              </template></v-autocomplete
            >
          </v-row>
        </v-form>
        <v-row v-if="!step1completed" align="start" justify="start">
          <v-btn
            :dark="isFormValid"
            :disabled="!isFormValid"
            @click="step1completed = true"
          >
            Continue
          </v-btn>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
export default {
  data() {
    return {
      fam_choice: "1",
      isFormValid: true,
      selected: [],
    };
  },
  methods: {
    ...mapActions(["fetchPollenHabits", "fetchTaxonFamilies"]),
    ...mapActions("db_fossilpercentages", [
      "setDefaultPollenSum",
      "resetState",
    ]),
    async loadData() {
      this.fetchPollenHabits();
      this.fetchTaxonFamilies();
    },
    required(value) {
      if (value instanceof Array && value.length == 0) {
        return "At least one pollen habit has to be added to selection. Otherwise, choose an other option.";
      }
      return !!value || "Required.";
    },
  },
  computed: {
    ...mapGetters(["getPollenHabits", "getTaxonFamilies"]),
    ...mapGetters("db_fossilpercentages", [
      "getDefaultPollenSum",
      "getPollenHabitsForm",
    ]),
    pollenhabitsvalidated() {
      return this.$store.getters["db_fossilpercentages/getPollenHabitsForm"]
        .validated;
    },
  },
  watch: {
    pollenhabitsvalidated(val) {
      if (!val) {
        this.getPollenHabitsForm.completed = false;
      }
    },
  },
  created() {
    this.loadData();
  },
};
</script>

<style>
</style>